<template>
  <div class="msp-workspace-treeview d-flex flex-column h-100">
    <template v-if="showSkeletonLoader">
      <v-sheet class="d-flex align-center pa-3">
        <v-skeleton-loader
          type="text"
          :width="180"
          class="ma-0 pa-0 bg-transparent"
        ></v-skeleton-loader>
        <v-spacer></v-spacer>
        <v-skeleton-loader
          type="avatar"
          size="24"
          class="ma-0 pa-0 bg-transparent"
        ></v-skeleton-loader>
      </v-sheet>

      <v-divider></v-divider>

      <div class="pa-2">
        <v-skeleton-loader type="list-item-avatar@4" class="bg-transparent"></v-skeleton-loader>

        <v-skeleton-loader type="list-item-avatar" class="bg-transparent"></v-skeleton-loader>
      </div>
    </template>

    <template v-else>
      <v-sheet class="d-flex align-center pa-3 msp-workspace-treeview__header">
        <v-checkbox
          v-model="selectAllModel"
          :indeterminate="selectAllModel === null"
          density="compact"
          hide-details
        >
          <template #label>
            <div v-if="selected.length" class="body3 text-indigo-medium">
              {{ $t("devices.selected", { n: selected.length }) }}
            </div>
            <div v-else class="subtitle1">
              {{ $t("manageWorkspaces.title") }}
            </div>
          </template>
        </v-checkbox>

        <v-spacer></v-spacer>

        <msp-bulk-actions-menu
          :context="ManageWorkspacesContext.SINGLE_HIERARCHY"
          :selected-items="selected"
          :workspace-store="workspaceStore"
          @on-bulk-action-applied="selected = []"
        />

        <v-btn class="ml-4" :icon="toggleIcon" density="compact" @click="toggleAll"></v-btn>

        <!-- Guarded on a non-empty tree: with nothing to collapse the panel would hide itself
             while no workspace is selected, and the expand control lives in the preview header,
             which only renders for a loaded workspace - leaving no way back. -->
        <v-btn
          v-if="items.length"
          v-tooltip:top="{ text: $t('mspWorkspaceTreeview.collapsePanel'), openDelay: 300 }"
          class="ml-4"
          icon="$hideNavigationXs"
          density="compact"
          :aria-label="$t('mspWorkspaceTreeview.collapsePanel')"
          data-testid="collapse-tree-btn"
          @click="$emit('toggle-collapsed')"
        ></v-btn>
      </v-sheet>
      <v-divider></v-divider>

      <div
        class="d-flex align-center justify-center flex-column flex-grow-1 w-100"
        v-if="!items.length"
      >
        <v-icon icon="$fullDetailsL" :size="80" />
        <div class="body2 text-indigo-medium">
          {{ $t("mspWorkspaceTreeview.emptyDescription") }}
        </div>
        <div class="subtitle2 text-indigo-medium">"{{ search }}"</div>
      </div>

      <v-treeview
        v-else
        :items="items"
        v-model:activated="activatedModel"
        v-model:selected="selected"
        v-model:opened="openedModel"
        activatable
        item-children="children"
        item-value="workspaceId"
        item-title="displayName"
        density="compact"
        class="py-0 msp-workspace-treeview__tree"
        active-strategy="single-independent"
        select-strategy="independent"
        mandatory
        return-object
        expand-icon="$arrowRightXs"
        collapse-icon="$arrowDownXs"
      >
        <template #toggle="{ props: toggleProps, item }">
          <v-btn
            :class="{
              'visibility-hidden': hideToggleBtn(item),
            }"
            v-bind="toggleProps"
            variant="text"
            density="compact"
            :key="`${item.workspaceId}-toggle-action`"
          ></v-btn>
        </template>

        <template v-slot:prepend="{ isSelected, isIndeterminate, item }">
          <v-list-item-action start :key="`${item.workspaceId}-prepend-action`">
            <v-checkbox-btn
              :model-value="isSelected"
              :indeterminate="isIndeterminate"
              @click.stop="toggleSelection(item, isSelected)"
              variant="text"
              density="compact"
            ></v-checkbox-btn>
          </v-list-item-action>
        </template>

        <template v-slot:append="{ item }">
          <div
            v-if="item.type === WorkspaceType.CHANNEL"
            class="d-flex align-center"
            :key="`${item.workspaceId}-append`"
          >
            <div class="subtitle2 ms-2 text-indigo-medium">
              {{ item.descendantCount ?? 0 }}
            </div>
            <v-menu v-if="item.children?.length" offset-y location="bottom left">
              <template #activator="{ props }">
                <v-btn
                  icon
                  :ripple="false"
                  variant="plain"
                  v-bind="props"
                  :width="0"
                  :height="20"
                  class="cursor-pointer msp-workspace-treeview__list__menu"
                  :elevation="0"
                  @click.stop
                >
                  <v-icon icon="$dots" size="20" />
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="action in getActionsForNode(item)"
                  :key="`${action}-${item.workspaceId}`"
                  @click="onSelectActionClick(action, item)"
                >
                  <v-list-item-title>
                    {{ $t(`mspWorkspaceTreeview.actions.${action}`) }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>

        <template v-slot:title="{ item }">
          <div class="d-flex align-center" :key="`${item.workspaceId}-title`">
            <div class="rounded pa-1 mr-2" :class="`workspace-type-${item.type}-icon-wrapper`">
              <v-icon :icon="iconsMap[item.type]"></v-icon>
            </div>
            <span
              class="v-treeview-item__label ellipsis"
              :class="{
                subtitle2: item.type === WorkspaceType.CHANNEL,
                body2: item.type === WorkspaceType.CHILD,
              }"
              :title="item.displayName"
            >
              {{ item.displayName }}
            </span>
          </div>
        </template>
      </v-treeview>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, type PropType } from "vue";
import { WorkspaceType } from "@/constants/workplaces.ts";
import MspBulkActionsMenu from "@/components/msp/MspBulkActionsMenu.vue";
import {
  buildWorkspaceNodeMap,
  getWorkspaceDescendantIds,
  getWorkspaceParentIds,
  mapWorkspaceIdsToNodes,
} from "@/_helpers/msp-treeview.ts";
import type { useMspTreeviewStore, WorkspaceNode } from "@/_store/msp/msp-treeview.module.ts";
import { ManageWorkspacesContext, MspTreeviewSelectionAction } from "@/constants/msp.ts";
import { trackGtmEvent } from "@/_helpers/analytics";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";

const iconsMap: Record<WorkspaceType, string> = {
  [WorkspaceType.REGULAR]: "icon-regular-plan-s",
  [WorkspaceType.CHANNEL]: "icon-channel-xs",
  [WorkspaceType.CHILD]: "icon-bundle-xs",
};

export default defineComponent({
  components: {
    MspBulkActionsMenu,
  },
  props: {
    items: {
      type: Array as PropType<WorkspaceNode[]>,
      required: true,
    },
    activated: {
      type: String,
      default: "",
    },
    search: {
      type: String,
      default: "",
    },
    showSkeletonLoader: {
      type: Boolean,
      required: false,
    },
    workspaceStore: {
      type: Object as PropType<ReturnType<typeof useMspTreeviewStore>>,
      required: true,
    },
  },
  emits: {
    "update:activated": (_id: string) => true,
    "toggle-collapsed": () => true,
  },
  setup(props, { emit }) {
    const selected = ref<WorkspaceNode[]>([]);
    const opened = ref<string[]>([]);

    const nodesMapById = computed(() => buildWorkspaceNodeMap(props.items));

    const allSelectableIds = computed(() => Array.from(nodesMapById.value.keys()));
    const allSelectableNodes = computed(() => Array.from(nodesMapById.value.values()));
    const selectedIds = computed(() => selected.value.map((item) => item.workspaceId));

    const openedModel = computed<WorkspaceNode[]>({
      get() {
        return opened.value
          .map((id) => nodesMapById.value.get(id))
          .filter((node): node is WorkspaceNode => Boolean(node));
      },
      set(value) {
        opened.value = value.map((item) => item.workspaceId);
      },
    });

    const activatedModel = computed<WorkspaceNode[]>({
      get() {
        if (!props.activated) {
          return [];
        }
        const node = nodesMapById.value.get(props.activated);
        return node ? [node] : [];
      },
      set(value) {
        const firstItem = value[0];
        const id = firstItem?.workspaceId;

        if (id) {
          const isRootWorkspace = props.items[0].workspaceId === id;
          const workspaceType = isRootWorkspace
            ? GTMParams.ROOT_WORKSPACE_CLICK
            : startCase(firstItem?.type);
          trackGtmEvent(
            GtmAction.MSP_TREEVIEW,
            `${GTMParams.WORKSPACE_NAME_CLICK} - ${workspaceType}`
          );
        }

        emit("update:activated", id ?? "");
      },
    });

    const selectAllModel = computed({
      get() {
        if (selectedIds.value.length === 0) {
          return false; // None selected
        }
        if (selectedIds.value.length === allSelectableIds.value.length) {
          return true; // All selected
        }
        return null; // Indeterminate (some selected)
      },
      set(value) {
        if (value === true) {
          // Select all
          selected.value = [...allSelectableNodes.value];
        } else {
          // Deselect all (value is false or null from indeterminate click)
          selected.value = [];
        }
      },
    });

    const allParentIds = computed(() => getWorkspaceParentIds(props.items));

    const toggleIcon = computed(() => {
      return opened.value.length > 0 ? "$collapseAllXs" : "$expandAllXs";
    });

    const hideToggleBtn = (item: WorkspaceNode) => {
      return (
        item.type === WorkspaceType.CHILD ||
        (item.type === WorkspaceType.CHANNEL && item.descendantCount === 0)
      );
    };

    const toggleAll = () => {
      if (opened.value.length > 0) {
        opened.value = [];
      } else {
        opened.value = allParentIds.value;
      }
    };

    const getActionsForNode = (item: WorkspaceNode): MspTreeviewSelectionAction[] => {
      const actions: MspTreeviewSelectionAction[] = [];

      if (!item.children || item.children.length === 0) {
        return actions;
      }

      // We add this if there are ANY children
      actions.push(MspTreeviewSelectionAction.SELECT_ALL_DESCENDANTS);

      // We ONLY add this if there is at least one direct child of type 'CHILD' (Workspace)
      const hasDirectChildWorkspace = item.children.some(
        (child) => child.type === WorkspaceType.CHILD
      );

      if (hasDirectChildWorkspace) {
        actions.push(MspTreeviewSelectionAction.SELECT_ALL_CHILD_WORKSPACES);
      }

      return actions;
    };

    const onSelectActionClick = (action: MspTreeviewSelectionAction, item: WorkspaceNode) => {
      let idsToSelect: string[] = [];

      switch (action) {
        case MspTreeviewSelectionAction.SELECT_ALL_DESCENDANTS:
          idsToSelect = getWorkspaceDescendantIds(item);
          break;

        case MspTreeviewSelectionAction.SELECT_ALL_CHILD_WORKSPACES:
          if (item.children) {
            idsToSelect = item.children
              .filter((child) => child.type === WorkspaceType.CHILD)
              .map((child) => child.workspaceId);
          }
          break;
      }

      selected.value = mapWorkspaceIdsToNodes(idsToSelect, nodesMapById.value);
    };

    const toggleSelection = (item: WorkspaceNode, isSelected: boolean) => {
      if (isSelected) {
        selected.value = selected.value.filter(
          (selectedItem) => selectedItem.workspaceId !== item.workspaceId
        );
      } else {
        selected.value = [...selected.value, item];
      }
    };

    return {
      opened,
      selected,
      iconsMap,
      openedModel,
      activatedModel,
      selectAllModel,
      toggleIcon,
      hideToggleBtn,
      toggleAll,
      getActionsForNode,
      onSelectActionClick,
      toggleSelection,
      WorkspaceType,
      ManageWorkspacesContext,
      MspTreeviewSelectionAction,
    };
  },
});
</script>
<style lang="scss" scoped>
.msp-workspace-treeview {
  &__header {
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: rgb(var(--v-theme-surface));
    border-bottom: 1px solid rgb(var(--v-theme-indigo-faint));
  }

  &__list__menu:not(:active):not(:focus) {
    opacity: 0;
    transition: all 0.3s ease-in-out;
  }

  &__list__menu:active,
  &__list__menu:focus {
    opacity: 1 !important;
    transition: all 0.3s ease-in-out;
    margin-left: 10px;
    margin-right: 5px;
  }
}

.workspace-type-child-icon-wrapper {
  background: rgb(var(--v-theme-indigo-pale));
}
.workspace-type-channel-icon-wrapper {
  background: rgb(var(--v-theme-blue-pale));
}

:deep(.v-list-item-action--start) {
  margin-inline-end: 0 !important;
  margin-inline-start: 0 !important;
}

:deep(.v-treeview-item:hover) .msp-workspace-treeview__list__menu {
  opacity: 1 !important;
  transition: all 0.3s ease-in-out;
  margin-left: 10px;
  margin-right: 5px;
}
</style>
